/**
 * Shared display vocabulary for collections — the type label, the season window
 * and the phase pill, used by the list, the detail header and the form.
 */
import type { StatusTone } from "@flint/ui";

/**
 * Phase → `StatusPill` tone. `ended` is muted, not bad: a closed season is a
 * normal end-of-life, not a fault. Upcoming uses sky — the deck's `b-info`.
 */
export const PHASE_TONE = {
  live: "ok",
  upcoming: "sky",
  ended: "muted",
  archived: "muted",
} as const satisfies Record<string, StatusTone>;

export const PHASE_LABEL = {
  live: "Live",
  upcoming: "Upcoming",
  ended: "Ended",
  archived: "Archived",
} as const;

/** Spec 15 R1 — two line-sheet kinds. Taxonomy, not state: rendered as plain text. */
export const TYPE_LABEL = {
  core: "Core",
  seasonal: "Seasonal",
} as const;

/**
 * Formatted in UTC, deliberately.
 *
 * A season window is a **date**, not a moment, and the wire carries it as UTC
 * midnight. Rendering that in the viewer's zone shows the previous day anywhere
 * west of UTC — so a browser in New York would label a season "opens 31 Aug"
 * while the server, deciding phase on the IST calendar, holds it upcoming until
 * the 1st. Pinning the formatter to UTC keeps the label and the phase describing
 * the same day, and is identical to today's output for a viewer in India.
 */
const DAY = { day: "numeric", month: "short", timeZone: "UTC" } as const;

const shortDay = (iso: string) => new Date(iso).toLocaleDateString("en-IN", DAY);

/**
 * "1 Feb — 31 Jul", or "Always on" when there's no window at all.
 *
 * A core collection reads "Always on" rather than an em-dash: it has no window
 * *by definition*, and blank would read as data someone forgot to fill in.
 */
export function seasonWindow(from: string | null, to: string | null): string {
  if (!from && !to) return "Always on";
  const f = from ? shortDay(from) : null;
  const t = to ? shortDay(to) : null;
  if (f && t) return `${f} — ${t}`;
  return f ? `From ${f}` : `Until ${t}`;
}

/**
 * The phase pill's date context — "Upcoming — opens 1 Sep", "Live — ends
 * 30 Nov", "Ended 31 Jul".
 *
 * Returns `null` for anything with no dates to explain (core, archived), which
 * is the caller's signal to render the bare pill with no tooltip rather than an
 * empty one.
 */
export function phaseWithDates(
  phase: keyof typeof PHASE_LABEL,
  from: string | null,
  to: string | null,
): string | null {
  if (phase === "upcoming" && from) return `Upcoming — opens ${shortDay(from)}`;
  if (phase === "live" && to) return `Live — ends ${shortDay(to)}`;
  if (phase === "ended" && to) return `Ended ${shortDay(to)}`;
  return null;
}

/** `<input type="date">` wants `yyyy-mm-dd`; the wire carries full ISO. */
export function toDateInput(iso: string | null): string {
  return iso ? new Date(iso).toISOString().slice(0, 10) : "";
}

/** Date input → ISO at UTC midnight, or null when cleared. */
export function fromDateInput(value: string): string | null {
  return value ? new Date(`${value}T00:00:00.000Z`).toISOString() : null;
}
